import type {
  AttachmentListResponse,
  DocAccess,
  DocAccessMode,
  DocAccessRuleEdit,
  DocAccessSubjectDetail,
  DocAccessSubjectListResponse,
  DocAccessSubjectType,
  DocAccessTree,
  DocProductListResponse,
  CategoryListResponse,
  DocAiGeneratePayload,
  DocCategory,
  DocCategoryCreatePayload,
  DocCategoryUpdatePayload,
  DocCreatePayload,
  DocFeatureListResponse,
  DocPurpose,
  DocSolutionAreaCreatePayload,
  DocSolutionAreaRef,
  DocSolutionAreaUpdatePayload,
  DocStatus,
  DocSummary,
  DocType,
  DocUpdatePayload,
  PaginatedDocList,
  ReleaseListResponse,
  SingleDoc,
  SingleDocSummary,
  SolutionAreaListResponse,
} from "@xop/dashboard-shared";

import { api } from "../../lib/api.js";
import { env } from "../../lib/env.js";
import { tokenStore } from "../../lib/tokenStore.js";

/**
 * API client for the Documentation tab. All requests go to the dashboard
 * server's `/api/documentation/*` routes, which proxy to the support-center
 * Rails backend (the place that owns the DO Spaces bucket + ActiveStorage
 * + the `Documents::*` services). Errors flow through the standard `api`
 * wrapper so 401s trigger the same access-token refresh as everywhere else.
 *
 * The two multipart endpoints (`upload`, `replace_file`) bypass the JSON
 * `api` helper because we need to send raw FormData with the boundary
 * intact. We still re-use `tokenStore` for the Authorization header so
 * the auth contract is identical.
 */

const BASE = "/api/documentation";

export interface ListDocsArgs {
  q?: string;
  category_id?: string;
  status?: DocStatus | "all";
  page?: number;
  per_page?: number;
}

export function listDocs(args: ListDocsArgs): Promise<PaginatedDocList> {
  const qs = new URLSearchParams();
  if (args.q) qs.set("q", args.q);
  if (args.category_id && args.category_id !== "all") qs.set("category_id", args.category_id);
  if (args.status && args.status !== "all") qs.set("status", args.status);
  qs.set("page", String(args.page ?? 1));
  qs.set("per_page", String(args.per_page ?? 15));
  return api<PaginatedDocList>(`${BASE}/documents?${qs.toString()}`);
}

/**
 * Fetch the COMPLETE document list by walking every page.
 *
 * The support-center admin documents endpoint hard-caps `per_page` at 100
 * (`base_controller.rb` `[per_page, 100].min`), so a single "wide pull"
 * (`per_page: 500`) silently truncates to the first 100 once the catalogue
 * grows past that — which made the sidebar tree under-count every category
 * (e.g. a 17-doc Blog folder reading "2"). Paginate to completion so the
 * tree's per-category / per-release counts and the deep-link doc lookup
 * reflect the whole library, not just page one.
 */
export async function listAllDocs(
  args: Omit<ListDocsArgs, "page" | "per_page"> = {},
): Promise<DocSummary[]> {
  const perPage = 100; // the server's hard ceiling — biggest legal page
  const first = await listDocs({ ...args, page: 1, per_page: perPage });
  const out = [...first.data];
  const totalPages = Math.max(1, first.meta.total_pages);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await listDocs({ ...args, page, per_page: perPage });
    out.push(...next.data);
  }
  return out;
}

export function getDoc(idOrSlug: string | number): Promise<SingleDoc> {
  return api<SingleDoc>(`${BASE}/documents/${idOrSlug}`);
}

export function listCategories(): Promise<CategoryListResponse> {
  return api<CategoryListResponse>(`${BASE}/categories`);
}

export function createCategory(
  payload: DocCategoryCreatePayload,
): Promise<{ data: DocCategory }> {
  return api<{ data: DocCategory }>(`${BASE}/categories`, {
    method: "POST",
    body: payload,
  });
}

export function updateCategory(
  id: number,
  payload: DocCategoryUpdatePayload,
): Promise<{ data: DocCategory }> {
  return api<{ data: DocCategory }>(`${BASE}/categories/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

/**
 * Two flavours of category delete — the dialog asks the operator which
 * one to use:
 *   • `"destroy"` (default): every document inside is purged through
 *     `Documents::PurgeAndRemove` — files removed from `xop-storage`,
 *     AI Gateway vector entries dropped, doc rows discarded.
 *   • `"keep"`: documents survive, their `document_category_id` is
 *     nulled (move-to-Uncategorized).
 */
export type CategoryDocumentsMode = "destroy" | "keep";

export function deleteCategory(
  id: number | string,
  documentsMode: CategoryDocumentsMode = "destroy",
): Promise<void> {
  return api<void>(
    `${BASE}/categories/${id}?documents=${documentsMode}`,
    { method: "DELETE" },
  );
}

export function bulkDeleteCategories(
  ids: number[],
  documentsMode: CategoryDocumentsMode = "destroy",
): Promise<{
  data: {
    destroyed_ids: number[];
    count: number;
    documents_mode: CategoryDocumentsMode;
  };
}> {
  return api<{
    data: {
      destroyed_ids: number[];
      count: number;
      documents_mode: CategoryDocumentsMode;
    };
  }>(`${BASE}/categories/bulk_destroy`, {
    method: "DELETE",
    body: { ids, documents: documentsMode },
  });
}

/* ---------- solution areas (managed product-line taxonomy) ---------- */

/** List solution areas. Editors land on the Rails admin namespace (the proxy
 * switches on `documentation:edit`) so hidden pillars + `document_count` show;
 * viewers get the visible-only public list so the switcher still renders. */
export function listSolutionAreas(): Promise<SolutionAreaListResponse> {
  return api<SolutionAreaListResponse>(`${BASE}/solution-areas`);
}

export function createSolutionArea(
  payload: DocSolutionAreaCreatePayload,
): Promise<{ data: DocSolutionAreaRef }> {
  return api<{ data: DocSolutionAreaRef }>(`${BASE}/solution-areas`, {
    method: "POST",
    body: payload,
  });
}

export function updateSolutionArea(
  id: number,
  payload: DocSolutionAreaUpdatePayload,
): Promise<{ data: DocSolutionAreaRef }> {
  return api<{ data: DocSolutionAreaRef }>(`${BASE}/solution-areas/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

/** Delete a solution area. Unlike a category, this NEVER deletes documents —
 * it just removes the pillar and untags every doc that carried it. */
export function deleteSolutionArea(id: number | string): Promise<void> {
  return api<void>(`${BASE}/solution-areas/${id}`, { method: "DELETE" });
}

export function bulkDeleteSolutionAreas(
  ids: number[],
): Promise<{ data: { destroyed_ids: number[]; count: number } }> {
  return api<{ data: { destroyed_ids: number[]; count: number } }>(
    `${BASE}/solution-areas/bulk_destroy`,
    { method: "DELETE", body: { ids } },
  );
}

export function listReleases(): Promise<ReleaseListResponse> {
  return api<ReleaseListResponse>(`${BASE}/releases?per_page=100`);
}

export function listAttachments(idOrSlug: string | number): Promise<AttachmentListResponse> {
  return api<AttachmentListResponse>(`${BASE}/documents/${idOrSlug}/attachments`);
}

export function createDoc(payload: DocCreatePayload): Promise<SingleDocSummary> {
  return api<SingleDocSummary>(`${BASE}/documents`, {
    method: "POST",
    body: payload,
  });
}

export function updateDoc(
  idOrSlug: string | number,
  payload: DocUpdatePayload,
): Promise<SingleDocSummary> {
  return api<SingleDocSummary>(`${BASE}/documents/${idOrSlug}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteDoc(idOrSlug: string | number): Promise<void> {
  return api<void>(`${BASE}/documents/${idOrSlug}`, { method: "DELETE" });
}

/** "Remove from this version" (2026-10-31): take the document off ONE
 * release row — its pin, entitlement mapping and version tag — and leave it in
 * its Documentation folder. 409 `DOC_ON_RELEASE_LINE` when it is only on the
 * version through the release above it. */
export function detachDocFromRelease(docId: number, releaseId: number): Promise<void> {
  return api<void>(`${BASE}/documents/${docId}/releases/${releaseId}`, { method: "DELETE" });
}

export function bulkDeleteDocs(ids: number[]): Promise<{ deleted: number }> {
  return api<{ deleted: number }>(`${BASE}/documents/bulk_destroy`, {
    method: "DELETE",
    body: { ids },
  });
}

export function publishDoc(idOrSlug: string | number): Promise<SingleDocSummary> {
  return api<SingleDocSummary>(`${BASE}/documents/${idOrSlug}/publish`, {
    method: "POST",
    body: {},
  });
}

export function unpublishDoc(idOrSlug: string | number): Promise<SingleDocSummary> {
  return api<SingleDocSummary>(`${BASE}/documents/${idOrSlug}/unpublish`, {
    method: "POST",
    body: {},
  });
}

export function bumpVersion(
  idOrSlug: string | number,
  content: string,
): Promise<unknown> {
  return api(`${BASE}/documents/${idOrSlug}/versions`, {
    method: "POST",
    body: {
      version: { content, status: "published" },
      bump: "minor",
    },
  });
}

export function generateAiDoc(payload: DocAiGeneratePayload): Promise<SingleDoc> {
  return api<SingleDoc>(`${BASE}/documents/generate`, {
    method: "POST",
    body: payload,
  });
}

export function deleteAttachment(
  idOrSlug: string | number,
  attachmentId: number,
): Promise<void> {
  return api<void>(`${BASE}/documents/${idOrSlug}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}

/* ---------- multipart helpers (bypass JSON wrapper) ---------- */

export interface UploadArgs {
  file: File;
  title?: string;
  summary?: string;
  status: DocStatus;
  document_category_id?: string;
  default_release_id?: string;
  /** An explicit type beats the content-type guess — a release card
   * uploading an installation PDF names it a `guide`. */
  doc_type?: DocType;
  /** Audience, applied on create like the manual editor's panel does. The
   * mapping arrays are only sent when a scope is given, and portal-api
   * reconciles each one only when its key is present. */
  access_scope?: DocAccess["scope"];
  product_codes?: string[];
  feature_codes?: string[];
  release_ids?: number[];
  /** `training` files the upload as a release's training material rather
   *  than its documentation. Omitted ⇒ portal-api's default, `documentation`. */
  purpose?: DocPurpose;
}

/** Drag-and-drop / picker upload. The dashboard server forwards the raw
 * multipart body to the support-center, where Active Storage lands the
 * file in DigitalOcean Spaces (`xop-storage` bucket) and the AI gateway
 * picks it up for indexing. */
export async function uploadDoc(args: UploadArgs): Promise<SingleDoc> {
  const fd = new FormData();
  fd.append("files[]", args.file, args.file.name);
  if (args.title?.trim()) fd.append("title", args.title.trim());
  if (args.summary?.trim()) fd.append("summary", args.summary.trim());
  fd.append("status", args.status);
  if (args.document_category_id) fd.append("document_category_id", args.document_category_id);
  if (args.default_release_id) fd.append("default_release_id", args.default_release_id);
  if (args.doc_type) fd.append("doc_type", args.doc_type);
  if (args.purpose) fd.append("purpose", args.purpose);
  if (args.access_scope) {
    fd.append("access_scope", args.access_scope);
    // Rails reads `key[]` as an array; an EMPTY array must still send the
    // key so a reconcile clears rather than skips — hence the "" sentinel
    // Rails' `Array(params[:x])` turns into `[""]`, which the reconcile
    // strips as blank.
    const list = (key: string, values: readonly (string | number)[]) => {
      if (values.length === 0) fd.append(`${key}[]`, "");
      for (const v of values) fd.append(`${key}[]`, String(v));
    };
    list("product_codes", args.access_scope === "product" ? (args.product_codes ?? []) : []);
    list("feature_codes", args.access_scope === "feature" ? (args.feature_codes ?? []) : []);
    list("release_ids", args.access_scope === "release" ? (args.release_ids ?? []) : []);
  }

  return multipartFetch<SingleDoc>(`${BASE}/documents/upload`, "POST", fd);
}

export async function replaceFile(
  idOrSlug: string | number,
  file: File,
): Promise<SingleDoc> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return multipartFetch<SingleDoc>(`${BASE}/documents/${idOrSlug}/replace_file`, "PATCH", fd);
}

/**
 * Fetch the raw bytes of a document's primary attachment through the dashboard
 * server's binary passthrough (`GET /api/documentation/documents/:id/file`).
 * Routed server-side (not the cross-origin Spaces/ActiveStorage URL) so an
 * image/PDF isn't CORS-tainted when embedded into a canvas and the Tauri
 * desktop webview can read it. Used by the "Download PDF" builder
 * (`docPdf.ts`) for the image / text / pdf-passthrough modes.
 */
export async function fetchDocFileBytes(
  docId: number,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  return fetchDocBytes(`${BASE}/documents/${docId}/file`);
}

/** Bytes of the doc's faithful ORIGINAL PDF (`source_file`) — streamed through
 * this origin's `/source-file` passthrough so the cross-origin ActiveStorage
 * URL is never fetched by the browser (CORS + the Tauri `tauri://` origin), and
 * the request carries the bearer the passthrough's auth guard requires. */
export async function fetchDocSourceFileBytes(
  docId: number,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  return fetchDocBytes(`${BASE}/documents/${docId}/source-file`);
}

async function fetchDocBytes(
  path: string,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  const token = tokenStore.getAccess();
  const url = `${env.apiUrl}${path}`;
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* non-JSON error body — keep the HTTP status message */
    }
    throw new Error(msg);
  }
  const bytes = await res.arrayBuffer();
  return { bytes, contentType: res.headers.get("content-type") };
}

async function multipartFetch<T>(path: string, method: string, body: FormData): Promise<T> {
  const token = tokenStore.getAccess();
  const url = `${env.apiUrl}${path}`;
  const res = await fetch(url, {
    method,
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: "include",
    body,
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    const errBody = parsed as { error?: string; code?: string } | null;
    throw new Error(errBody?.error ?? `HTTP ${res.status}`);
  }
  return parsed as T;
}

/** The product catalog the document editor's access panel picks from. */
export async function listDocProducts(): Promise<DocProductListResponse> {
  return api<DocProductListResponse>(`${BASE}/products`);
}

/** The product-FEATURE catalog the access panel's "customers who own a
 * feature" picker uses (e.g. Mass Notification). Read-only CRM mirror. */
export async function listDocFeatures(): Promise<DocFeatureListResponse> {
  return api<DocFeatureListResponse>(`${BASE}/features`);
}

/** Every document that ships with a release — the documents PINNED to it
 * (`default_release_id`; a library document merely mapped to the release for
 * its audience is not one, 2026-10-31) — every status, with each one's
 * audience. Feeds the version pane on CRM ▸ Releases. */
export async function listReleaseDocuments(releaseId: number): Promise<DocSummary[]> {
  const res = await api<{ data?: DocSummary[] }>(`${BASE}/admin/releases/${releaseId}/documents`);
  return res.data ?? [];
}

/** A version's TRAINING material — the PDFs and videos filed with
 * `purpose: "training"` — every status. Same endpoint as the documentation
 * list with `?purpose=training`; without it portal-api lists documentation
 * only, which is what keeps the two sections of the version pane apart. */
export async function listReleaseTrainings(releaseId: number): Promise<DocSummary[]> {
  const res = await api<{ data?: DocSummary[] }>(
    `${BASE}/admin/releases/${releaseId}/documents?purpose=training`,
  );
  return res.data ?? [];
}

/* ---------- documentation access (who sees which documents) ---------- */

/**
 * The access surface answers a different question from the rest of this
 * client. Everything above reads the LIBRARY — one catalog, the same for
 * every staff member. These read an ACCOUNT's view of that library, so they
 * name a subject in the path.
 *
 * That subject is not a tenant filter on the caller. The documentation proxy
 * is contractually forbidden from narrowing staff reads to one organization
 * (that mistake once blanked the whole tab for a sales_manager), and these
 * routes do not do it: the staff library stays whole, and the subject is the
 * thing being edited.
 */
/**
 * How a subject is addressed. A customer is its portal-api organization id.
 * A reseller is EITHER its portal-api `reseller_accounts.id` (what the
 * Access page's rail hands back) OR `crm-<crm reseller id>` — the form the
 * CRM reseller profile uses, because that page knows only the crm-api id
 * and mirroring the portal-api id into the CRM would be a second identity
 * to keep in step. portal-api resolves both (2026-09-14).
 */
export type DocAccessSubjectId = number | string;

export async function listAccessSubjects(params: {
  type: DocAccessSubjectType;
  q?: string;
  page?: number;
  per_page?: number;
}): Promise<DocAccessSubjectListResponse> {
  const query = new URLSearchParams({ type: params.type });
  if (params.q) query.set("q", params.q);
  if (params.page) query.set("page", String(params.page));
  if (params.per_page) query.set("per_page", String(params.per_page));

  return api<DocAccessSubjectListResponse>(`${BASE}/access/subjects?${query.toString()}`);
}

export async function getAccessSubject(
  type: DocAccessSubjectType,
  id: DocAccessSubjectId,
): Promise<{ data: DocAccessSubjectDetail }> {
  return api<{ data: DocAccessSubjectDetail }>(`${BASE}/access/${type}/${id}`);
}

export async function getAccessTree(
  type: DocAccessSubjectType,
  id: DocAccessSubjectId,
): Promise<{ data: DocAccessTree }> {
  return api<{ data: DocAccessTree }>(`${BASE}/access/${type}/${id}/tree`);
}

/**
 * Save the whole edited ruleset in ONE call. Upstream applies it in a single
 * transaction — an access control that is half-applied for a few hundred
 * milliseconds is a different kind of problem from a form that saves in
 * pieces.
 */
export async function saveAccessRules(
  type: DocAccessSubjectType,
  id: DocAccessSubjectId,
  rules: DocAccessRuleEdit[],
): Promise<{ data: DocAccessSubjectDetail }> {
  return api<{ data: DocAccessSubjectDetail }>(`${BASE}/access/${type}/${id}/rules`, {
    method: "PUT",
    // `api()` stringifies the body itself — every other call in this file
    // passes a plain object. Stringifying here too put a JSON STRING on the
    // wire, which Fastify parsed as a string and the proxy re-stringified,
    // so Rails saw no `rules` key at all and the save was a silent no-op.
    body: { rules },
  });
}

export async function setAccessMode(
  type: DocAccessSubjectType,
  id: DocAccessSubjectId,
  mode: DocAccessMode,
): Promise<{ data: DocAccessSubjectDetail }> {
  return api<{ data: DocAccessSubjectDetail }>(`${BASE}/access/${type}/${id}`, {
    method: "PATCH",
    body: { doc_access_mode: mode },
  });
}
